import React, { useState, useMemo } from 'react'
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  AlertCircle,
  Cable,
  Split,
  CircleDot,
  Zap
} from 'lucide-react'
import { 
  RatioSplitter, 
  TipoSplitter, 
  RatioDesbalanceado,
  NivelSplitter,
  PuertoNAP
} from '../../types/nodes'
import { ColorFibra, ColorBuffer, Cable as CableType } from '../../types/fibers'
import { 
  crearSangrado, 
  ParamsSangrado,
  ResultadoSangrado,
  TIA_598_COLORES,
  ATENUACION_SPLITTER,
  ATENUACION_DESBALANCEADO,
  posicionAColor
} from '../../services/connectionService'

interface SangradoEditorProps {
  nodoId: string
  tipoNodo: 'CIERRE' | 'NAP'
  cablesDisponibles: CableType[]
  puertosNAP?: PuertoNAP[]
  onSave: (resultado: ResultadoSangrado) => void
  onCancel: () => void
}

type WizardStep = 'cable' | 'fibra' | 'splitter' | 'destinos' | 'confirmar'

const WIZARD_STEPS: { id: WizardStep; label: string; icon: React.ReactNode }[] = [
  { id: 'cable', label: 'Cable Origen', icon: <Cable className="w-4 h-4" /> },
  { id: 'fibra', label: 'Fibra a Sangrar', icon: <CircleDot className="w-4 h-4" /> },
  { id: 'splitter', label: 'Config. Splitter', icon: <Split className="w-4 h-4" /> },
  { id: 'destinos', label: 'Destinos', icon: <Zap className="w-4 h-4" /> },
  { id: 'confirmar', label: 'Confirmar', icon: <Check className="w-4 h-4" /> }
]

const COLOR_MAP: Record<string, string> = {
  azul: '#0066CC',
  naranja: '#FF6600',
  verde: '#00AA00',
  marron: '#8B4513',
  gris: '#808080',
  blanco: '#F5F5F5',
  rojo: '#CC0000',
  negro: '#1a1a1a',
  amarillo: '#FFD700',
  violeta: '#8B008B',
  rosa: '#FF69B4',
  turquesa: '#00CED1'
}

export const SangradoEditor: React.FC<SangradoEditorProps> = ({
  nodoId,
  tipoNodo,
  cablesDisponibles,
  puertosNAP = [],
  onSave,
  onCancel
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('cable')
  const [error, setError] = useState<string | null>(null)
  
  const [selectedCable, setSelectedCable] = useState<string>('')
  const [selectedTubo, setSelectedTubo] = useState<number>(1)
  const [selectedHilo, setSelectedHilo] = useState<number>(1)
  
  const [tipoSplitter, setTipoSplitter] = useState<TipoSplitter>('balanceado')
  const [ratioSplitter, setRatioSplitter] = useState<RatioSplitter>('1:8')
  const [ratioDesbalanceado, setRatioDesbalanceado] = useState<RatioDesbalanceado>('80/20')
  const [nivelSplitter, setNivelSplitter] = useState<NivelSplitter>(tipoNodo === 'CIERRE' ? 1 : 2)
  
  const [destinos, setDestinos] = useState<Array<{
    puertoSplitter: number
    tipo: 'empalme_cable' | 'puerto_nap' | 'cliente_directo'
    cableId?: string
    bufferNum?: number
    fibraNum?: number
    puertoNapNum?: number
    clienteOntId?: string
    nombreCliente?: string
  }>>([])

  const numSalidasSplitter = useMemo(() => {
    // Un splitter desbalanceado (tap) siempre tiene 2 salidas físicas: cascada y local.
    // `ratioSplitter` (1:2..1:64) solo aplica a splitters balanceados.
    if (tipoSplitter === 'desbalanceado') return 2
    return parseInt(ratioSplitter.split(':')[1])
  }, [tipoSplitter, ratioSplitter])

  const atenuacionCalculada = useMemo(() => {
    if (tipoSplitter === 'balanceado') {
      return ATENUACION_SPLITTER[ratioSplitter]
    } else {
      return ATENUACION_DESBALANCEADO[ratioDesbalanceado]
    }
  }, [tipoSplitter, ratioSplitter, ratioDesbalanceado])

  const colorTubo = useMemo(() => posicionAColor(selectedTubo), [selectedTubo])
  const colorHilo = useMemo(() => posicionAColor(selectedHilo), [selectedHilo])

  const stepIndex = WIZARD_STEPS.findIndex(s => s.id === currentStep)

  const canGoNext = () => {
    switch (currentStep) {
      case 'cable':
        return !!selectedCable
      case 'fibra':
        return selectedTubo > 0 && selectedHilo > 0
      case 'splitter':
        return true
      case 'destinos':
        return true
      case 'confirmar':
        return true
      default:
        return false
    }
  }

  const handleNext = () => {
    if (!canGoNext()) return
    
    const nextIndex = stepIndex + 1
    if (nextIndex < WIZARD_STEPS.length) {
      setCurrentStep(WIZARD_STEPS[nextIndex].id)
    }
  }

  const handleBack = () => {
    const prevIndex = stepIndex - 1
    if (prevIndex >= 0) {
      setCurrentStep(WIZARD_STEPS[prevIndex].id)
    }
  }

  const handleSave = () => {
    try {
      setError(null)
      
      if (!selectedCable) {
        throw new Error('Seleccione un cable de origen')
      }
      
      const params: ParamsSangrado = {
        nodoId,
        tipoNodo,
        cableOrigenId: selectedCable,
        tuboOrigen: selectedTubo,
        hiloOrigen: selectedHilo,
        colorTubo: colorTubo as ColorBuffer,
        colorHilo: colorHilo as ColorFibra,
        splitterConfig: {
          tipo: tipoSplitter,
          ratio: ratioSplitter,
          ratioDesbalanceado: tipoSplitter === 'desbalanceado' ? ratioDesbalanceado : undefined,
          nivel: nivelSplitter
        },
        destinos
      }
      
      const resultado = crearSangrado(params)
      onSave(resultado)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear sangrado')
    }
  }

  const addDestino = (puertoSplitter: number) => {
    if (destinos.some(d => d.puertoSplitter === puertoSplitter)) return
    
    setDestinos([
      ...destinos,
      {
        puertoSplitter,
        tipo: tipoNodo === 'NAP' ? 'puerto_nap' : 'empalme_cable'
      }
    ])
  }

  const updateDestino = (
    puertoSplitter: number, 
    updates: Partial<typeof destinos[0]>
  ) => {
    setDestinos(destinos.map(d => 
      d.puertoSplitter === puertoSplitter ? { ...d, ...updates } : d
    ))
  }

  const removeDestino = (puertoSplitter: number) => {
    setDestinos(destinos.filter(d => d.puertoSplitter !== puertoSplitter))
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'cable':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Seleccione el cable del cual se tomará la fibra para el sangrado:
            </p>
            
            <div className="grid gap-2 max-h-60 overflow-y-auto">
              {cablesDisponibles.length === 0 ? (
                <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg">
                  No hay cables conectados a este nodo
                </div>
              ) : (
                cablesDisponibles.map(cable => (
                  <button
                    key={cable.id}
                    onClick={() => setSelectedCable(cable.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-colors ${
                      selectedCable === cable.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{cable.id}</p>
                        <p className="text-sm text-gray-500">
                          {cable.tipo} • {cable.numeroFibras} fibras
                        </p>
                      </div>
                      {selectedCable === cable.id && (
                        <Check className="w-5 h-5 text-blue-500" />
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )
        
      case 'fibra':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Seleccione el tubo y el hilo específico a sangrar:
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tubo (Buffer)
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {TIA_598_COLORES.map((color, idx) => (
                    <button
                      key={`tubo-${idx}`}
                      onClick={() => setSelectedTubo(idx + 1)}
                      className={`p-2 rounded-lg border-2 text-center transition-colors ${
                        selectedTubo === idx + 1
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: COLOR_MAP[color] + '33' }}
                    >
                      <div
                        className="w-6 h-6 rounded-full mx-auto mb-1 border"
                        style={{ 
                          backgroundColor: COLOR_MAP[color],
                          borderColor: color === 'blanco' ? '#ccc' : COLOR_MAP[color]
                        }}
                      />
                      <span className="text-xs">{idx + 1}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Tubo seleccionado: <span className="font-medium">{selectedTubo} ({colorTubo})</span>
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hilo (Fibra)
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {TIA_598_COLORES.map((color, idx) => (
                    <button
                      key={`hilo-${idx}`}
                      onClick={() => setSelectedHilo(idx + 1)}
                      className={`p-2 rounded-lg border-2 text-center transition-colors ${
                        selectedHilo === idx + 1
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: COLOR_MAP[color] + '33' }}
                    >
                      <div
                        className="w-6 h-6 rounded-full mx-auto mb-1 border"
                        style={{ 
                          backgroundColor: COLOR_MAP[color],
                          borderColor: color === 'blanco' ? '#ccc' : COLOR_MAP[color]
                        }}
                      />
                      <span className="text-xs">{idx + 1}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Hilo seleccionado: <span className="font-medium">{selectedHilo} ({colorHilo})</span>
                </p>
              </div>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Fibra a sangrar:</strong> Cable {selectedCable} | 
                Tubo {selectedTubo} ({colorTubo}) | 
                Hilo {selectedHilo} ({colorHilo})
              </p>
            </div>
          </div>
        )
        
      case 'splitter':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Configure el splitter que recibirá la fibra sangrada:
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Splitter
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTipoSplitter('balanceado')}
                    className={`flex-1 p-3 rounded-lg border-2 ${
                      tipoSplitter === 'balanceado'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium">Balanceado</p>
                    <p className="text-xs text-gray-500">Misma potencia en todas las salidas</p>
                  </button>
                  <button
                    onClick={() => setTipoSplitter('desbalanceado')}
                    className={`flex-1 p-3 rounded-lg border-2 ${
                      tipoSplitter === 'desbalanceado'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium">Desbalanceado</p>
                    <p className="text-xs text-gray-500">Potencia diferente por puerto</p>
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nivel
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNivelSplitter(1)}
                    className={`flex-1 p-3 rounded-lg border-2 ${
                      nivelSplitter === 1
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium">1er Nivel</p>
                    <p className="text-xs text-gray-500">En Cierre/Mufa</p>
                  </button>
                  <button
                    onClick={() => setNivelSplitter(2)}
                    className={`flex-1 p-3 rounded-lg border-2 ${
                      nivelSplitter === 2
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="font-medium">2do Nivel</p>
                    <p className="text-xs text-gray-500">En NAP</p>
                  </button>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ratio de División
              </label>
              {tipoSplitter === 'balanceado' ? (
                <div className="grid grid-cols-6 gap-2">
                  {(['1:2', '1:4', '1:8', '1:16', '1:32', '1:64'] as RatioSplitter[]).map(ratio => (
                    <button
                      key={ratio}
                      onClick={() => setRatioSplitter(ratio)}
                      className={`p-2 rounded-lg border-2 text-center ${
                        ratioSplitter === ratio
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <p className="font-medium">{ratio}</p>
                      <p className="text-xs text-gray-500">{ATENUACION_SPLITTER[ratio]}dB</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {(['90/10', '85/15', '80/20', '70/30', '60/40', '50/50'] as RatioDesbalanceado[]).map(ratio => {
                    const att = ATENUACION_DESBALANCEADO[ratio]
                    return (
                      <button
                        key={ratio}
                        onClick={() => setRatioDesbalanceado(ratio)}
                        className={`p-2 rounded-lg border-2 text-center ${
                          ratioDesbalanceado === ratio
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200'
                        }`}
                      >
                        <p className="font-medium">{ratio}</p>
                        <p className="text-xs text-gray-500">
                          {att.alto}dB / {att.bajo}dB
                        </p>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Resumen:</strong> Splitter {tipoSplitter} {tipoSplitter === 'desbalanceado' ? ratioDesbalanceado : ratioSplitter} (Nivel {nivelSplitter})
                {' • '}
                Atenuación: {typeof atenuacionCalculada === 'number' 
                  ? `${atenuacionCalculada} dB` 
                  : `${atenuacionCalculada.alto}/${atenuacionCalculada.bajo} dB`
                }
                {' • '}
                Salidas: {numSalidasSplitter}
              </p>
            </div>
          </div>
        )
        
      case 'destinos':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Configure el destino de cada puerto de salida del splitter:
            </p>
            
            <div className="grid gap-2 max-h-64 overflow-y-auto">
              {Array.from({ length: numSalidasSplitter }, (_, i) => i + 1).map(puerto => {
                const destino = destinos.find(d => d.puertoSplitter === puerto)
                const colorPuerto = posicionAColor(puerto)
                
                return (
                  <div 
                    key={`destino-${puerto}`}
                    className="p-3 rounded-lg border border-gray-200 bg-white"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full border"
                          style={{ 
                            backgroundColor: COLOR_MAP[colorPuerto],
                            borderColor: colorPuerto === 'blanco' ? '#ccc' : COLOR_MAP[colorPuerto]
                          }}
                        />
                        <span className="font-medium">Puerto {puerto}</span>
                        <span className="text-xs text-gray-500">({colorPuerto})</span>
                      </div>
                      
                      {!destino ? (
                        <button
                          onClick={() => addDestino(puerto)}
                          className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          + Asignar
                        </button>
                      ) : (
                        <button
                          onClick={() => removeDestino(puerto)}
                          className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200"
                        >
                          × Quitar
                        </button>
                      )}
                    </div>
                    
                    {destino && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <select
                          value={destino.tipo}
                          onChange={(e) => updateDestino(puerto, { 
                            tipo: e.target.value as any,
                            cableId: undefined,
                            puertoNapNum: undefined
                          })}
                          className="text-sm p-1.5 border rounded"
                        >
                          {tipoNodo === 'NAP' && (
                            <option value="puerto_nap">Puerto NAP</option>
                          )}
                          <option value="empalme_cable">Empalme a Cable</option>
                          <option value="cliente_directo">Cliente Directo</option>
                        </select>
                        
                        {destino.tipo === 'puerto_nap' && (
                          <select
                            value={destino.puertoNapNum || ''}
                            onChange={(e) => updateDestino(puerto, { 
                              puertoNapNum: parseInt(e.target.value) 
                            })}
                            className="text-sm p-1.5 border rounded"
                          >
                            <option value="">Seleccionar puerto...</option>
                            {puertosNAP
                              .filter(p => p.estado === 'libre')
                              .map(p => (
                                <option key={p.puerto} value={p.puerto}>
                                  Puerto {p.puerto}
                                </option>
                              ))
                            }
                          </select>
                        )}
                        
                        {destino.tipo === 'empalme_cable' && (
                          <select
                            value={destino.cableId || ''}
                            onChange={(e) => updateDestino(puerto, { 
                              cableId: e.target.value 
                            })}
                            className="text-sm p-1.5 border rounded"
                          >
                            <option value="">Seleccionar cable...</option>
                            {cablesDisponibles
                              .filter(c => c.id !== selectedCable)
                              .map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.id}
                                </option>
                              ))
                            }
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            
            <p className="text-xs text-gray-500">
              Puertos configurados: {destinos.length} de {numSalidasSplitter}. 
              Los puertos sin asignar quedarán disponibles para uso futuro.
            </p>
          </div>
        )
        
      case 'confirmar':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Revise la configuración antes de crear el sangrado:
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span className="text-gray-600">Cable Origen:</span>
                <span className="font-medium">{selectedCable}</span>
              </div>
              
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span className="text-gray-600">Fibra a Sangrar:</span>
                <span className="font-medium">
                  Tubo {selectedTubo} ({colorTubo}) / Hilo {selectedHilo} ({colorHilo})
                </span>
              </div>
              
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span className="text-gray-600">Splitter:</span>
                <span className="font-medium">
                  {tipoSplitter} {tipoSplitter === 'desbalanceado' ? ratioDesbalanceado : ratioSplitter} (Nivel {nivelSplitter})
                </span>
              </div>
              
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span className="text-gray-600">Atenuación:</span>
                <span className="font-medium">
                  {typeof atenuacionCalculada === 'number' 
                    ? `${atenuacionCalculada} dB` 
                    : `${atenuacionCalculada.alto}/${atenuacionCalculada.bajo} dB`
                  }
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Destinos Configurados:</span>
                <span className="font-medium">{destinos.length} puertos</span>
              </div>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Importante:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>La fibra seleccionada será cortada y marcada como "derivada"</li>
                    <li>El lado hacia el cliente será marcado como "muerto"</li>
                    <li>Esta acción creará {destinos.length + 2} conexiones internas</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Crear Sangrado de Fibra
            </h2>
            <p className="text-sm text-gray-500">
              {tipoNodo} - {nodoId}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex border-b overflow-x-auto">
          {WIZARD_STEPS.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => idx <= stepIndex && setCurrentStep(step.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                step.id === currentStep
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : idx < stepIndex
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500'
              }`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                step.id === currentStep
                  ? 'bg-blue-500 text-white'
                  : idx < stepIndex
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {idx < stepIndex ? <Check className="w-3 h-3" /> : idx + 1}
              </span>
              <span className="hidden sm:inline">{step.label}</span>
            </button>
          ))}
        </div>
        
        <div className="p-4 min-h-[300px]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          {renderStepContent()}
        </div>
        
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={handleBack}
            disabled={stepIndex === 0}
            className={`flex items-center gap-1 px-4 py-2 rounded-lg ${
              stepIndex === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </button>
          
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
            >
              Cancelar
            </button>
            
            {currentStep === 'confirmar' ? (
              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Check className="w-4 h-4" />
                Crear Sangrado
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canGoNext()}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg ${
                  canGoNext()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SangradoEditor
